<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    :scrollable="scrollable"
    :persistent="persistent"
    :content-class="dialogContentClass"
    @input="$emit('input', $event)"
  >
    <v-card class="rmr-modal__card">
      <div
        v-if="variant === 'gradient'"
        class="rmr-modal__header rmr-modal__header--gradient"
      >
        <div class="rmr-modal__header-main">
          <div class="rmr-modal__icon-box" :style="iconStyle">
            <v-icon color="white" :size="28">{{ icon }}</v-icon>
          </div>
          <div class="rmr-modal__copy">
            <div class="rmr-modal__eyebrow">{{ eyebrow }}</div>
            <div class="rmr-modal__title">{{ title }}</div>
            <slot name="header-subtitle" />
          </div>
        </div>
        <div class="rmr-modal__header-right">
          <slot name="header-actions" />
          <v-btn
            v-if="closable"
            icon
            class="rmr-modal__close rmr-modal__close--on-dark"
            @click="onClose"
          >
            <v-icon color="white">{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-else class="rmr-modal__header rmr-modal__header--form">
        <div class="rmr-modal__header-main">
          <div
            v-if="icon"
            class="rmr-modal__icon-box rmr-modal__icon-box--form"
            :style="iconStyle"
          >
            <v-icon :color="indigoColor" :size="24">{{ icon }}</v-icon>
          </div>
          <div class="rmr-modal__copy">
            <div class="rmr-modal__eyebrow rmr-modal__eyebrow--form">
              {{ eyebrow }}
            </div>
            <div class="rmr-modal__title rmr-modal__title--form">
              {{ title }}
            </div>
            <div v-if="subtitle" class="rmr-modal__subtitle">
              {{ subtitle }}
            </div>
            <slot name="header-subtitle" />
          </div>
        </div>
        <div class="rmr-modal__header-right">
          <slot name="header-actions" />
          <v-btn
            v-if="closable"
            icon
            class="rmr-modal__close rmr-modal__close--on-light"
            @click="onClose"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </div>
      </div>

      <slot name="body"></slot>
      <div
        v-if="hasFooter"
        class="rmr-modal__footer"
        :class="`rmr-modal__footer--${variant}`"
      >
        <slot name="footer"></slot>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiClose } from "@mdi/js";
import { RMR_INDIGO } from "@/styles/tokens";

export default {
  name: "RmrModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "gradient",
      validator: (value) => ["gradient", "form"].includes(value),
    },
    icon: {
      type: String,
      default: "",
    },
    eyebrow: {
      type: String,
      default: "Market Intelligence",
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: "",
    },
    iconStyle: {
      type: Object,
      default: () => ({}),
    },
    maxWidth: {
      type: [String, Number],
      default: 800,
    },
    scrollable: {
      type: Boolean,
      default: true,
    },
    contentClass: {
      type: String,
      default: "",
    },
    // A blocking operation: no outside click, Escape or close button.
    persistent: {
      type: Boolean,
      default: false,
    },
    closable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      icons: {
        mdiClose,
      },
      indigoColor: RMR_INDIGO,
    };
  },
  computed: {
    dialogContentClass() {
      return `rmr-modal${this.contentClass ? " " + this.contentClass : ""}`;
    },
    hasFooter() {
      return Boolean(this.$slots.footer || this.$scopedSlots.footer);
    },
  },
  methods: {
    onClose() {
      this.$emit("close");
      this.$emit("input", false);
    },
  },
};
</script>

<style lang="scss" scoped>
.rmr-modal__card {
  border-radius: $rmr-radius-2xl !important;
  overflow: hidden;
}

.rmr-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px;

  &-main {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    min-width: 0;
  }

  &-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
}

.rmr-modal__header--gradient {
  background: linear-gradient(
    135deg,
    $rmr-indigo-dark 0%,
    $rmr-indigo-accent 40%,
    $rmr-indigo 100%
  );
}

.rmr-modal__icon-box {
  width: 56px;
  height: 56px;
  border-radius: $rmr-radius-2xl;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rmr-modal__copy {
  min-width: 0;
}

.rmr-modal__eyebrow {
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.62);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}

.rmr-modal__title {
  font-size: 24px;
  font-weight: 800;
  color: white;
  line-height: 1.2;
}

.rmr-modal__header--form {
  background: linear-gradient(135deg, $rmr-indigo-bg 0%, $rmr-surface-2 100%);
  border-bottom: 1px solid $rmr-border-hover;
  position: sticky;
  top: 0;
  z-index: 4;
  padding: 22px 24px 18px;
}

.rmr-modal__icon-box--form {
  width: 52px;
  height: 52px;
  background: linear-gradient(
    135deg,
    rgba($rmr-indigo, 0.12),
    rgba($rmr-indigo, 0.06)
  );
  border: none;
  box-shadow: 0 4px 12px rgba($rmr-indigo, 0.18);
}

.rmr-modal__eyebrow--form {
  color: $rmr-indigo;
}

.rmr-modal__title--form {
  color: $rmr-indigo-dark;
}

.rmr-modal__subtitle {
  font-size: 13px;
  color: $rmr-text-muted;
  margin-top: 2px;
}

.rmr-modal__footer {
  flex-shrink: 0;

  ::v-deep .v-card__actions {
    align-items: center;
    margin: 0;
    min-height: 0;
  }
}

.rmr-modal__footer--gradient {
  border-top: 1px solid rgba($rmr-indigo, 0.12);
  background: linear-gradient(
    180deg,
    rgba($rmr-indigo, 0.02) 0%,
    rgba($rmr-indigo, 0.06) 100%
  );
}

.rmr-modal__footer--form {
  border-top: 1px solid $rmr-border-hover;
}

::v-deep .v-card__text {
  @include rmr-scrollbar;
}

.rmr-modal__close {
  width: 40px !important;
  height: 40px !important;
  border-radius: $rmr-radius-lg !important;

  &::before {
    opacity: 0 !important;
  }

  &--on-dark {
    background: rgba(255, 255, 255, 0.14) !important;

    &:hover {
      background: rgba(255, 255, 255, 0.24) !important;
    }
  }

  &--on-light {
    background: rgba(255, 255, 255, 0.7) !important;
    border: 1px solid rgba($rmr-indigo, 0.12) !important;

    &:hover {
      background: white !important;
    }
  }
}

@media (max-width: 960px) {
  .rmr-modal__header {
    padding-left: 16px;
    padding-right: 16px;
  }

  .rmr-modal__header-main {
    gap: 12px;
  }

  .rmr-modal__icon-box {
    width: 48px;
    height: 48px;
  }

  .rmr-modal__title {
    font-size: 20px;
  }
}
</style>
